import { useBaseStore } from '@/store';
import { Store } from '@/store/type';
import {
  ArrowRight,
  Clipboard,
  User,
  ShieldUser,
  Clock,
  CheckCircle2,
  Flame,
  ChevronRight,
  Building2,
  Users,
  SquareArrowOutUpRight,
  RefreshCw,
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { useUserDashboardSummaryQuery } from '@/queries/useUserDashboardSummaryQuery';
import { TypographyH1, TypographyMuted } from '@/components/ui/typography';
import { Button } from '@/components/ui/button';

const stateSelector = (state: Store) => ({
  user: state.authSlice.state.user,
  profile: state.authSlice.state.profile,
});

const Home = () => {
  const navigate = useNavigate();
  const { user, profile } = useBaseStore(stateSelector);
  const {
    data: summaryData,
    isLoading,
    isFetching,
    refetch,
  } = useUserDashboardSummaryQuery();

  const pendingCount = summaryData?.pendingCount ?? 0;
  const totalAssigned = summaryData?.totalAssigned ?? 0;
  const priorityResearches = summaryData?.priorityResearches ?? [];

  return (
    <div className="flex flex-col h-full overflow-auto p-4 md:p-6 bg-gray-50/80 space-y-6">
      {/* Header Section */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-white p-5 rounded-2xl border border-gray-200/80 shadow-sm">
        <div>
          <TypographyH1 className="text-2xl font-bold text-gray-900 tracking-tight">
            Olá, <span className="text-primary">{user}</span> 👋
          </TypographyH1>
          <TypographyMuted className="text-sm text-gray-500 mt-1">
            Acompanhe o resumo das suas pesquisas e priorize as demandas em
            aberto.
          </TypographyMuted>
        </div>
        <div className="flex items-center gap-2 self-start sm:self-auto">
          <Button
            variant="outline"
            size="sm"
            onClick={() => refetch()}
            disabled={isFetching}
            className="gap-2 text-gray-700 bg-gray-50 hover:bg-gray-100 border-gray-200 shadow-none rounded-xl"
          >
            <RefreshCw
              size={14}
              className={isFetching ? 'animate-spin text-primary' : ''}
            />
            <span>Atualizar</span>
          </Button>
        </div>
      </div>

      {/* Indicator Cards Section (RF002 / RF004) - Visível apenas para Administradores */}
      {profile !== 'REPRESENTANTE' && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* Card 1: Pesquisas Pendentes */}
          <div
            onClick={() => navigate('/market-research?status=Publicado')}
            className="cursor-pointer bg-white rounded-2xl p-5 border border-amber-200/80 shadow-sm hover:shadow-md transition-all hover:border-amber-300 group"
          >
            <div className="flex items-center justify-between mb-3">
              <div className="w-10 h-10 rounded-xl bg-amber-50 text-amber-600 flex items-center justify-center font-bold">
                <Clock size={20} />
              </div>
              <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-amber-100/70 text-amber-700 group-hover:bg-amber-200/70 transition-all flex items-center gap-1">
                Ação requerida <ChevronRight size={12} />
              </span>
            </div>
            <div className="text-3xl font-extrabold text-gray-900 tracking-tight mb-1">
              {isLoading ? '...' : pendingCount}
            </div>
            <div className="text-sm font-semibold text-gray-800">
              Pesquisas Pendentes
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Pesquisas ativas aguardando execução ou finalização
            </p>
          </div>

          {/* Card 2: Pesquisas Não Realizadas */}

          {/* Card 3: Total de Pesquisas Atribuídas */}
          <div
            onClick={() => navigate('/market-research?status=all')}
            className="cursor-pointer bg-white rounded-2xl p-5 border border-blue-200/80 shadow-sm hover:shadow-md transition-all hover:border-blue-300 group"
          >
            <div className="flex items-center justify-between mb-3">
              <div className="w-10 h-10 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center font-bold">
                <CheckCircle2 size={20} />
              </div>
              <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-blue-100/70 text-blue-700 group-hover:bg-blue-200/70 transition-all flex items-center gap-1">
                Ver todas <ChevronRight size={12} />
              </span>
            </div>
            <div className="text-3xl font-extrabold text-gray-900 tracking-tight mb-1">
              {isLoading ? '...' : totalAssigned}
            </div>
            <div className="text-sm font-semibold text-gray-800">
              Total Atribuídas
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Total de pesquisas no seu escopo de acesso
            </p>
          </div>
        </div>
      )}

      {/* Priority Section: Priorização por Maior Tempo em Aberto (RF003) */}
      <div className="bg-white rounded-2xl border border-gray-200/80 shadow-sm p-5 space-y-4">
        <div className="flex items-center justify-between border-b border-gray-100 pb-3">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-orange-100 text-orange-600 flex items-center justify-center">
              <Flame size={18} />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900 tracking-tight">
                Priorização de Pesquisas (Maior Tempo em Aberto)
              </h2>
              <p className="text-xs text-gray-500">
                Pesquisas pendentes destacadas visualmente pelo tempo decorrido
                desde o início
              </p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/market-research')}
            className="text-xs text-primary font-semibold hover:bg-primary/5 gap-1"
          >
            Ver tudo <ArrowRight size={14} />
          </Button>
        </div>

        {isLoading ? (
          <div className="py-8 text-center text-sm text-gray-400">
            Carregando pesquisas prioritárias...
          </div>
        ) : priorityResearches.length === 0 ? (
          <div className="py-8 text-center bg-gray-50/50 rounded-xl border border-dashed border-gray-200 p-6">
            <CheckCircle2 size={32} className="mx-auto text-emerald-500 mb-2" />
            <p className="text-sm font-semibold text-gray-700">
              Nenhuma pesquisa pendente no momento!
            </p>
            <p className="text-xs text-gray-500 mt-0.5">
              Suas atividades atribuídas estão em dia.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {priorityResearches.map((item) => {
              const progress =
                item.QTD_CLIENTES_TOTAL > 0
                  ? Math.min(
                      100,
                      Math.round(
                        (item.QTD_CLIENTES_PESQUISADOS /
                          item.QTD_CLIENTES_TOTAL) *
                          100,
                      ),
                    )
                  : 0;

              return (
                <div
                  key={item.SK_PESQUISA}
                  className="bg-white rounded-xl border border-gray-200 p-4 hover:border-primary/50 shadow-sm hover:shadow transition-all flex flex-col justify-between space-y-3"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="space-y-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-bold text-gray-900 text-base leading-snug">
                          {item.NM_PESQUISA}
                        </h3>
                      </div>
                      {item.DS_REVENDA && (
                        <p className="text-xs text-gray-500 font-medium">
                          {item.DS_REVENDA}
                        </p>
                      )}
                    </div>

                    {/* Priority Badge */}
                    {item.PRIORITY_LEVEL === 'ALTA' ? (
                      <span className="shrink-0 px-2.5 py-1 rounded-full text-xs font-bold bg-rose-100 text-rose-700 border border-rose-200 flex items-center gap-1">
                        <Flame size={12} /> {item.DAYS_OPEN} dias em aberto
                      </span>
                    ) : item.PRIORITY_LEVEL === 'MEDIA' ? (
                      <span className="shrink-0 px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-100 text-amber-800 border border-amber-200 flex items-center gap-1">
                        <Clock size={12} /> {item.DAYS_OPEN} dias em aberto
                      </span>
                    ) : (
                      <span className="shrink-0 px-2.5 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-700 border border-slate-200">
                        {item.DAYS_OPEN} dias em aberto
                      </span>
                    )}
                  </div>

                  {/* Progress & Metrics */}
                  <div className="space-y-1.5 pt-1">
                    <div className="flex items-center justify-between text-xs font-medium text-gray-600">
                      <span>
                        Progresso Clientes ({item.QTD_CLIENTES_PESQUISADOS}/
                        {item.QTD_CLIENTES_TOTAL})
                      </span>
                      <span className="font-bold text-gray-900">
                        {progress}%
                      </span>
                    </div>
                    <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden">
                      <div
                        className={`h-full transition-all duration-300 ${
                          progress === 100
                            ? 'bg-emerald-500'
                            : item.PRIORITY_LEVEL === 'ALTA'
                              ? 'bg-rose-500'
                              : 'bg-primary'
                        }`}
                        style={{ width: `${progress}%` }}
                      />
                    </div>
                  </div>

                  {/* Footer Action */}
                  <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                    <span className="text-xs text-gray-400">
                      Ciclo:{' '}
                      <strong className="text-gray-600 font-semibold">
                        {item.DD_TIPO_CICLO || 'Padrão'}
                      </strong>
                    </span>
                    <Button
                      size="sm"
                      onClick={() =>
                        navigate(
                          `/market-research/details?search=${item.SK_PESQUISA}`,
                        )
                      }
                      className="h-8 px-3 text-xs gap-1.5 font-semibold rounded-lg shadow-sm"
                    >
                      <SquareArrowOutUpRight size={12} /> Ver Pesquisa
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Quick Navigation Cards Section */}
      <div className="space-y-3">
        <h2 className="text-base font-bold text-gray-900 tracking-tight">
          Módulos & Atalhos
        </h2>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <Link
            to={'/market-research'}
            className="w-full bg-gradient-to-br from-primary-600 to-primary-800 rounded-2xl p-4 shadow-md hover:shadow-lg transition-all text-left group flex flex-col justify-between"
          >
            <div className="flex items-start justify-between mb-3">
              <div className="w-10 h-10 rounded-xl bg-white bg-opacity-20 flex items-center justify-center">
                <Clipboard className="w-5 h-5 text-white" />
              </div>
              <div className="w-8 h-8 rounded-full bg-white bg-opacity-20 flex items-center justify-center group-hover:bg-opacity-30 transition-all">
                <ArrowRight className="w-5 h-5 text-white" />
              </div>
            </div>
            <div>
              <h3 className="text-lg font-bold text-white mb-1">
                Planejamento de Pesquisa
              </h3>
              <p className="text-emerald-50 text-xs leading-relaxed">
                Acompanhe e efetue suas pesquisas de mercado
              </p>
            </div>
          </Link>

          {['ADMINISTRADOR', 'ADMINISTRADOR_REVENDA'].includes(profile!) && (
            <>
              <Link
                to={'/market-research/reseller-coverage'}
                className="w-full bg-gradient-to-br from-emerald-600 to-teal-800 rounded-2xl p-4 shadow-md hover:shadow-lg transition-all text-left group flex flex-col justify-between"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="w-10 h-10 rounded-xl bg-white bg-opacity-20 flex items-center justify-center">
                    <Building2 className="w-5 h-5 text-white" />
                  </div>
                  <div className="w-8 h-8 rounded-full bg-white bg-opacity-20 flex items-center justify-center group-hover:bg-opacity-30 transition-all">
                    <ArrowRight className="w-5 h-5 text-white" />
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-white mb-1">
                    Cobertura por Revenda
                  </h3>
                  <p className="text-teal-50 text-xs leading-relaxed">
                    Relatório consolidado de cobertura por revenda
                  </p>
                </div>
              </Link>

              <Link
                to={'/market-research/seller-coverage'}
                className="w-full bg-gradient-to-br from-blue-600 to-indigo-800 rounded-2xl p-4 shadow-md hover:shadow-lg transition-all text-left group flex flex-col justify-between"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="w-10 h-10 rounded-xl bg-white bg-opacity-20 flex items-center justify-center">
                    <Users className="w-5 h-5 text-white" />
                  </div>
                  <div className="w-8 h-8 rounded-full bg-white bg-opacity-20 flex items-center justify-center group-hover:bg-opacity-30 transition-all">
                    <ArrowRight className="w-5 h-5 text-white" />
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-white mb-1">
                    Cobertura por Vendedor
                  </h3>
                  <p className="text-blue-50 text-xs leading-relaxed">
                    Indicadores e progresso individual por vendedor
                  </p>
                </div>
              </Link>
            </>
          )}

          {profile === 'ADMINISTRADOR' && (
            <>
              <Link
                to={'/access/user'}
                className="w-full bg-gradient-to-br from-slate-700 to-slate-900 rounded-2xl p-4 shadow-md hover:shadow-lg transition-all text-left group flex flex-col justify-between"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="w-10 h-10 rounded-xl bg-white bg-opacity-20 flex items-center justify-center">
                    <User className="w-5 h-5 text-white" />
                  </div>
                  <div className="w-8 h-8 rounded-full bg-white bg-opacity-20 flex items-center justify-center group-hover:bg-opacity-30 transition-all">
                    <ArrowRight className="w-5 h-5 text-white" />
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-white mb-1">
                    Usuários
                  </h3>
                  <p className="text-slate-200 text-xs leading-relaxed">
                    Gestão e cadastro de usuários do sistema
                  </p>
                </div>
              </Link>

              <Link
                to={'/access/user-permissions'}
                className="w-full bg-gradient-to-br from-purple-700 to-indigo-900 rounded-2xl p-4 shadow-md hover:shadow-lg transition-all text-left group flex flex-col justify-between"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="w-10 h-10 rounded-xl bg-white bg-opacity-20 flex items-center justify-center">
                    <ShieldUser className="w-5 h-5 text-white" />
                  </div>
                  <div className="w-8 h-8 rounded-full bg-white bg-opacity-20 flex items-center justify-center group-hover:bg-opacity-30 transition-all">
                    <ArrowRight className="w-5 h-5 text-white" />
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-white mb-1">
                    Permissões de Acesso
                  </h3>
                  <p className="text-purple-100 text-xs leading-relaxed">
                    Manutenção de perfis e acessos por revenda
                  </p>
                </div>
              </Link>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Home;
